import type { Blueprint, BlueprintRevisions } from 'types/api.types';
import { getRepositoryUrl } from './vcs.utils';

const AzureDevopsDomain = 'dev.azure.com';
const BitbucketDomain = 'bitbucket.org';
const GithubDomain = 'github.com';
const GitlabDomain = 'gitlab.com';

const getBitbucketLines = (startLine?: number, endLine?: number): string => {
  if (!startLine) return '';
  const lineFragment = endLine && endLine !== startLine ? `${startLine}:${endLine}` : `${startLine}`;
  return `#lines-${lineFragment}`;
};

const getAzureDevOpsLines = (startLine?: number, endLine?: number): string => {
  if (!startLine) return '';
  return endLine && endLine !== startLine ? `&line=${startLine}&lineEnd=${endLine}` : `&line=${startLine}`;
};

const getGitHubGitLabLines = (startLine?: number, endLine?: number): string => {
  if (!startLine) return '';
  return endLine && endLine !== startLine ? `#L${startLine}-L${endLine}` : `#L${startLine}`;
};

const commitHashPattern = /^[0-9a-f]{7,40}$/i;

// Azure DevOps prefixes a version with its kind: GT tag, GB branch, GC commit. A revision missing from a
// loaded list was typed, so it is a commit; with no list, a commit-shaped hex string is.
const getAzureDevOpsVersion = (revision: string, revisions?: BlueprintRevisions, isTag = false) => {
  if (isTag || revisions?.tags.includes(revision)) return `GT${revision}`;
  if (revisions?.heads.includes(revision)) return `GB${revision}`;
  return `${revisions || commitHashPattern.test(revision) ? 'GC' : 'GB'}${revision}`;
};

export const getTemplateProjectScopeId = (template?: Blueprint): string | undefined =>
  template?.creationScope === 'PROJECT' ? template.creationScopeId : undefined;

export const buildRepoUrl = ({
  repository,
  revision,
  url,
  path,
  isTag,
  revisions,
  startLine,
  endLine
}: {
  repository: string;
  revision: string;
  url?: string;
  path?: string;
  isTag: boolean;
  revisions?: BlueprintRevisions;
  startLine?: number;
  endLine?: number;
}) => {
  if (repository.includes(BitbucketDomain)) {
    return `${url}${revision ? `/src/${revision}/${path}` : ''}${getBitbucketLines(startLine, endLine)}`;
  } else if (repository.includes(AzureDevopsDomain)) {
    return `${url}?path=/${path}&version=${getAzureDevOpsVersion(revision, revisions, isTag)}${getAzureDevOpsLines(startLine, endLine)}`;
  } else {
    return `${url}${revision ? `/tree/${revision}/${path}` : ''}${getGitHubGitLabLines(startLine, endLine)}`;
  }
};

export const getGitRevisionUrl = ({
  repository,
  revision,
  path,
  isTag = false,
  revisions
}: {
  repository: string;
  revision?: string;
  path?: string;
  isTag?: boolean;
  revisions?: BlueprintRevisions;
}) => {
  const url = getRepositoryUrl(repository);
  const fixedRevision = revision ?? (repository.includes(AzureDevopsDomain) ? 'main' : path ? 'master' : '');
  const fixedPath = path ?? '';
  const builtUrl = buildRepoUrl({ repository, revision: fixedRevision, url, path: fixedPath, isTag, revisions });
  return builtUrl.endsWith('/') || repository.includes(AzureDevopsDomain) ? builtUrl : `${builtUrl}/`;
};

export const getGitPrUrl = (repository: string, prNumber: string) => {
  const url = getRepositoryUrl(repository);
  const separator = repository.includes(GithubDomain)
    ? 'pull'
    : repository.includes(GitlabDomain)
      ? '-/merge_requests'
      : repository.includes(BitbucketDomain)
        ? 'pull-requests'
        : repository.includes(AzureDevopsDomain)
          ? 'pullrequest'
          : '';
  const builtUrl = [url, separator, prNumber].filter(_ => _).join('/');

  return builtUrl.endsWith('/') ? builtUrl : `${builtUrl}/`;
};

export const getGitCommitUrl = (blueprint: Blueprint, commitHash: string) => {
  const url = getRepositoryUrl(blueprint.repository);
  const separator =
    blueprint.isGitHubEnterprise || blueprint.githubInstallationId
      ? 'commit'
      : blueprint.isGitLabEnterprise || blueprint.isGitLab
        ? '-/commit'
        : blueprint.isBitbucketServer || blueprint.bitbucketClientKey
          ? 'commits'
          : blueprint.isAzureDevOps
            ? 'commit'
            : '';
  const builtUrl = [url, separator, commitHash].filter(_ => _).join('/');

  return builtUrl.endsWith('/') ? builtUrl : `${builtUrl}/`;
};

export const getGitCommitDiffUrl = ({
  blueprint,
  baseRevision,
  headRevision,
  revisions
}: {
  blueprint: Blueprint;
  baseRevision: string;
  headRevision: string;
  revisions?: BlueprintRevisions;
}) => {
  const url = getRepositoryUrl(blueprint.repository);

  const diffHash =
    blueprint.isGitHubEnterprise || blueprint.githubInstallationId
      ? `compare/${baseRevision}...${headRevision}`
      : blueprint.isGitLabEnterprise || blueprint.isGitLab
        ? `-/compare/${baseRevision}...${headRevision}`
        : blueprint.isBitbucketServer || blueprint.bitbucketClientKey
          ? `branches/compare/${baseRevision}..${headRevision}`
          : blueprint.isAzureDevOps
            ? `/branchCompare?baseVersion=${getAzureDevOpsVersion(baseRevision, revisions)}&targetVersion=${getAzureDevOpsVersion(headRevision, revisions)}`
            : '';
  const builtUrl = [url, diffHash].filter(_ => _).join('/');

  // Azure DevOps link MUST NOT end with '/' :facepalm:
  // But others (Bitbucket for sure) has to end with '/'
  return builtUrl.endsWith('/') || blueprint.isAzureDevOps ? builtUrl : `${builtUrl}/`;
};
